/**
 * Shipments UI Types
 * Frontend mirror of backend types from @lognai/plugins/shipments
 */

import type { ListQueryParams } from '@lognai/frontend-core';

// ============================================================================
// Type Unions (String Literals)
// ============================================================================

export type ShipmentType = 'fcl' | 'lcl' | 'air' | 'road' | 'roro' | 'break_bulk';
export type ShipmentDirection = 'import' | 'export' | 'domestic';
export type ShipmentStatus = 
  | 'draft'
  | 'booked'
  | 'in_transit'
  | 'arrived'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'on_hold';

export type LegStatus = 'planned' | 'in_transit' | 'arrived' | 'completed' | 'cancelled';
export type TransportMode = 'sea' | 'air' | 'road' | 'rail' | 'barge';
export type ContainerSize = '20ft' | '40ft' | '40hc' | '45hc' | 'other';
export type ContainerType = 'dry' | 'reefer' | 'open_top' | 'flat_rack' | 'tank' | 'other';
export type ContainerStatus = 
  | 'empty'
  | 'loaded'
  | 'in_transit'
  | 'arrived'
  | 'delivered'
  | 'returned';

export type ShipmentDocumentType =
  | 'bill_of_lading'
  | 'house_bill_of_lading'
  | 'master_bill_of_lading'
  | 'sea_waybill'
  | 'airway_bill'
  | 'cmr_waybill'
  | 'commercial_invoice'
  | 'packing_list'
  | 'certificate_of_origin'
  | 'insurance_certificate'
  | 'customs_declaration'
  | 'dangerous_goods_declaration'
  | 'delivery_order'
  | 'shipping_instruction'
  | 'other';

export type DocumentStatus = 
  | 'draft'
  | 'issued'
  | 'submitted'
  | 'approved'
  | 'expired'
  | 'cancelled';

export type TrackingEventType =
  | 'booking_confirmed'
  | 'cargo_received'
  | 'loaded'
  | 'departed'
  | 'in_transit'
  | 'transshipment'
  | 'arrived'
  | 'unloaded'
  | 'customs_cleared'
  | 'out_for_pickup'
  | 'out_for_delivery'
  | 'delivered'
  | 'exception'
  | 'on_hold'
  | 'released'
  | 'other';

export type PartyRole =
  | 'shipper'
  | 'consignee'
  | 'carrier'
  | 'freight_forwarder'
  | 'customs_broker'
  | 'notify_party';

export type Priority = 'low' | 'normal' | 'high' | 'urgent';

export type Incoterm =
  | 'EXW'
  | 'FCA'
  | 'CPT'
  | 'CIP'
  | 'DAP'
  | 'DPU'
  | 'DDP'
  | 'FAS'
  | 'FOB'
  | 'CFR'
  | 'CIF';

// ============================================================================
// Embedded Subdocument Interfaces
// ============================================================================

export interface ShipmentParty {
  role: PartyRole;
  companyId?: string;
  companyName: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
}

export interface HazardousDetails {
  unNumber?: string;
  class?: string;
  packingGroup?: string;
  properShippingName?: string;
  emergencyContact?: string;
}

export type WeightUnit = 'kg' | 'lb' | 'mt';
export type VolumeUnit = 'cbm' | 'cft';
export type TemperatureUnit = 'celsius' | 'fahrenheit';

export interface CargoDetails {
  description?: string;
  hsCode?: string;
  packageCount?: number;
  packageType?: string;
  grossWeight?: number;
  grossWeightUnit?: WeightUnit;
  volume?: number;
  volumeUnit?: VolumeUnit;
  chargeableWeight?: number;
}

// ============================================================================
// Entity Interfaces
// ============================================================================

export interface Shipment {
  id: string;
  companyId: string;
  shipmentNumber: string;
  type: ShipmentType;
  direction: ShipmentDirection;
  status: ShipmentStatus;
  priority?: Priority;
  parties?: ShipmentParty[];
  originLocation: string;
  originCountry: string;
  destinationLocation: string;
  destinationCountry: string;
  bookingDate?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  deliveryDate?: string;
  /** POD discharge (journeyStatus 18). Starts the demurrage clock. */
  dischargeDate?: string;
  /** Gate-out for delivery (journeyStatus 19). Ends demurrage, starts detention. */
  gateOutDate?: string;
  /** Empty container returned (journeyStatus 21). Ends detention. */
  emptyReturnDate?: string;
  cargo?: CargoDetails;
  isHazardous: boolean;
  hazardousDetails?: HazardousDetails;
  incoterm?: Incoterm;
  currency?: string;
  freightCost?: number;
  insuranceCost?: number;
  totalCost?: number;
  chargesTotal?: number;
  orderIds?: string[];
  customerReference?: string;
  carrierReference?: string;
  /** Carrier SCAC — links the shipment to the carrier master (uppercased). */
  carrierScac?: string;
  bookingReference?: string;
  legCount: number;
  containerCount: number;
  documentCount: number;
  trackingEventCount: number;
  /**
   * Journey phase (0–21) derived from tracking milestones — the most-advanced
   * ACTual milestone reached. Advance-only. Drives the radar lane.
   */
  trackingPhase?: number;
  notes?: string;
  internalNotes?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** Carrier support flags (which reference numbers the carrier honors). */
export interface CarrierSupports {
  billOfLading: boolean;
  bookingNumber: boolean;
  containerNumber: boolean;
}

/** Global carrier reference (shipping line). Mirrors the shipments backend `Carrier`. */
export interface Carrier {
  id: string;
  scac: string; // primary SCAC, uppercased
  aliases: string[]; // additional SCACs, uppercased
  name: string;
  type: 'ocean';
  region?: string;
  supports: CarrierSupports;
  notes?: string;
  active: boolean;
}

export interface ShipmentLeg {
  id: string;
  companyId: string;
  shipmentId: string;
  legNumber: number;
  status: LegStatus;
  transportMode: TransportMode;
  carrierId?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  flightNumber?: string;
  vehiclePlate?: string;
  originLocation: string;
  originCountry: string;
  destinationLocation: string;
  destinationCountry: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentContainer {
  id: string;
  companyId: string;
  shipmentId: string;
  containerNumber: string;
  sealNumber?: string;
  size: ContainerSize;
  containerType: ContainerType;
  status: ContainerStatus;
  grossWeight?: number;
  grossWeightUnit?: string;
  tareWeight?: number;
  maxPayload?: number;
  isTemperatureControlled: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentDocumentAttachment {
  id: string;
  s3Key: string;
  s3Bucket: string;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
}

export interface ShipmentDocument {
  id: string;
  companyId: string;
  shipmentId: string;
  documentType: ShipmentDocumentType;
  referenceNumber: string;
  title: string;
  status: DocumentStatus;
  issuedDate?: string;
  expiryDate?: string;
  issuedBy?: string;
  attachments: ShipmentDocumentAttachment[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentTrackingEvent {
  id: string;
  companyId: string;
  shipmentId: string;
  legId?: string;
  eventType: TrackingEventType;
  description: string;
  location?: string;
  country?: string;
  eventDate: string;
  source?: string;
  sourceReference?: string;
  notes?: string;
  /**
   * Provider-supplied extras. Ocean-tracking events carry
   * `latitude`/`longitude`/`vesselName` here; manual events have none.
   */
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// ============================================================================
// DTO Interfaces
// ============================================================================

export interface CreateShipmentDto {
  type: ShipmentType;
  direction: ShipmentDirection;
  priority?: Priority;
  parties?: ShipmentParty[];
  originLocation: string;
  originCountry: string;
  destinationLocation: string;
  destinationCountry: string;
  bookingDate?: string;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  cargo?: CargoDetails;
  isHazardous?: boolean;
  hazardousDetails?: HazardousDetails;
  incoterm?: Incoterm;
  currency?: string;
  freightCost?: number;
  insuranceCost?: number;
  orderIds?: string[];
  customerReference?: string;
  carrierReference?: string;
  carrierScac?: string;
  bookingReference?: string;
  notes?: string;
  internalNotes?: string;
}

export interface UpdateShipmentDto {
  direction?: ShipmentDirection;
  priority?: Priority;
  parties?: ShipmentParty[];
  originLocation?: string;
  originCountry?: string;
  destinationLocation?: string;
  destinationCountry?: string;
  bookingDate?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  deliveryDate?: string;
  cargo?: CargoDetails;
  isHazardous?: boolean;
  hazardousDetails?: HazardousDetails;
  incoterm?: Incoterm;
  currency?: string;
  freightCost?: number;
  insuranceCost?: number;
  orderIds?: string[];
  customerReference?: string;
  carrierReference?: string;
  carrierScac?: string;
  bookingReference?: string;
  notes?: string;
  internalNotes?: string;
}

export interface UpdateShipmentStatusDto {
  status: ShipmentStatus;
  reason?: string;
}

export interface CreateShipmentLegDto {
  transportMode: TransportMode;
  carrierId?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  flightNumber?: string;
  vehiclePlate?: string;
  originLocation: string;
  originCountry: string;
  destinationLocation: string;
  destinationCountry: string;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  notes?: string;
}

export interface UpdateShipmentLegDto {
  status?: LegStatus;
  carrierId?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  flightNumber?: string;
  vehiclePlate?: string;
  originLocation?: string;
  originCountry?: string;
  destinationLocation?: string;
  destinationCountry?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  notes?: string;
}

export interface CreateShipmentContainerDto {
  containerNumber: string;
  sealNumber?: string;
  size: ContainerSize;
  containerType: ContainerType;
  grossWeight?: number;
  grossWeightUnit?: string;
  tareWeight?: number;
  maxPayload?: number;
  isTemperatureControlled?: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit?: string;
  notes?: string;
}

export interface UpdateShipmentContainerDto {
  sealNumber?: string;
  size?: ContainerSize;
  containerType?: ContainerType;
  status?: ContainerStatus;
  grossWeight?: number;
  grossWeightUnit?: string;
  tareWeight?: number;
  maxPayload?: number;
  isTemperatureControlled?: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit?: string;
  notes?: string;
}

export interface CreateShipmentDocumentDto {
  documentType: ShipmentDocumentType;
  referenceNumber: string;
  title: string;
  issuedDate?: string;
  expiryDate?: string;
  issuedBy?: string;
  notes?: string;
}

export interface UpdateShipmentDocumentDto {
  referenceNumber?: string;
  title?: string;
  status?: DocumentStatus;
  issuedDate?: string;
  expiryDate?: string;
  issuedBy?: string;
  notes?: string;
}

export interface CreateShipmentTrackingEventDto {
  eventType: TrackingEventType;
  description: string;
  location?: string;
  country?: string;
  eventDate: string;
  legId?: string;
  source?: string;
  sourceReference?: string;
  notes?: string;
}

// ============================================================================
// Filter Interfaces
// ============================================================================

export interface ShipmentFilter {
  status?: ShipmentStatus;
  type?: ShipmentType;
  direction?: ShipmentDirection;
  priority?: Priority;
  search?: string;
}

export type ShipmentFilters = ListQueryParams<ShipmentFilter>;

export interface ShipmentKpis {
  totalShipments: number;
  draftCount: number;
  bookedCount: number;
  inTransitCount: number;
  arrivedCount: number;
  deliveredCount: number;
  completedCount: number;
  cancelledCount: number;
  onHoldCount: number;
}

// ============================================================================
// Label Maps (for UI Display)
// ============================================================================

export const SHIPMENT_TYPE_LABELS: Record<ShipmentType, string> = {
  fcl: 'FCL',
  lcl: 'LCL',
  air: 'Air',
  road: 'Road',
  roro: 'RORO',
  break_bulk: 'Break Bulk',
};

export const SHIPMENT_DIRECTION_LABELS: Record<ShipmentDirection, string> = {
  import: 'Import',
  export: 'Export',
  domestic: 'Domestic',
};

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  draft: 'Draft',
  booked: 'Booked',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  sea: 'Sea',
  air: 'Air',
  road: 'Road',
  rail: 'Rail',
  barge: 'Barge',
};

export const LEG_STATUS_LABELS: Record<LegStatus, string> = {
  planned: 'Planned',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const CONTAINER_SIZE_LABELS: Record<ContainerSize, string> = {
  '20ft': '20ft',
  '40ft': '40ft',
  '40hc': '40ft HC',
  '45hc': '45ft HC',
  other: 'Other',
};

export const CONTAINER_TYPE_LABELS: Record<ContainerType, string> = {
  dry: 'Dry',
  reefer: 'Reefer',
  open_top: 'Open Top',
  flat_rack: 'Flat Rack',
  tank: 'Tank',
  other: 'Other',
};

export const CONTAINER_STATUS_LABELS: Record<ContainerStatus, string> = {
  empty: 'Empty',
  loaded: 'Loaded',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  delivered: 'Delivered',
  returned: 'Returned',
};

export const DOCUMENT_TYPE_LABELS: Record<ShipmentDocumentType, string> = {
  bill_of_lading: 'Bill of Lading',
  house_bill_of_lading: 'House Bill of Lading',
  master_bill_of_lading: 'Master Bill of Lading',
  sea_waybill: 'Sea Waybill',
  airway_bill: 'Airway Bill',
  cmr_waybill: 'CMR Waybill',
  commercial_invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  certificate_of_origin: 'Certificate of Origin',
  insurance_certificate: 'Insurance Certificate',
  customs_declaration: 'Customs Declaration',
  dangerous_goods_declaration: 'Dangerous Goods Declaration',
  delivery_order: 'Delivery Order',
  shipping_instruction: 'Shipping Instruction',
  other: 'Other',
};

// Common document types available for all shipment modes
const COMMON_DOCUMENT_TYPES: ShipmentDocumentType[] = [
  'commercial_invoice', 'packing_list', 'certificate_of_origin',
  'insurance_certificate', 'customs_declaration', 'dangerous_goods_declaration',
  'delivery_order', 'shipping_instruction', 'other',
];

// Document types relevant to each shipment type
export const DOCUMENT_TYPES_BY_SHIPMENT_TYPE: Record<string, ShipmentDocumentType[]> = {
  fcl: ['bill_of_lading', 'house_bill_of_lading', 'master_bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
  lcl: ['bill_of_lading', 'house_bill_of_lading', 'master_bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
  air: ['airway_bill', ...COMMON_DOCUMENT_TYPES],
  road: ['cmr_waybill', ...COMMON_DOCUMENT_TYPES],
  roro: ['bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
  break_bulk: ['bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  submitted: 'Submitted',
  approved: 'Approved',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export const TRACKING_EVENT_TYPE_LABELS: Record<TrackingEventType, string> = {
  booking_confirmed: 'Booking Confirmed',
  cargo_received: 'Cargo Received',
  loaded: 'Loaded',
  departed: 'Departed',
  in_transit: 'In Transit',
  transshipment: 'Transshipment',
  arrived: 'Arrived',
  unloaded: 'Unloaded',
  customs_cleared: 'Customs Cleared',
  out_for_pickup: 'Out for Pickup',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  exception: 'Exception',
  on_hold: 'On Hold',
  released: 'Released',
  other: 'Other',
};

export const PARTY_ROLE_LABELS: Record<PartyRole, string> = {
  shipper: 'Shipper',
  consignee: 'Consignee',
  carrier: 'Carrier',
  freight_forwarder: 'Freight Forwarder',
  customs_broker: 'Customs Broker',
  notify_party: 'Notify Party',
};

export const INCOTERM_LABELS: Record<Incoterm, string> = {
  EXW: 'EXW - Ex Works',
  FCA: 'FCA - Free Carrier',
  CPT: 'CPT - Carriage Paid To',
  CIP: 'CIP - Carriage and Insurance Paid To',
  DAP: 'DAP - Delivered at Place',
  DPU: 'DPU - Delivered at Place Unloaded',
  DDP: 'DDP - Delivered Duty Paid',
  FAS: 'FAS - Free Alongside Ship',
  FOB: 'FOB - Free on Board',
  CFR: 'CFR - Cost and Freight',
  CIF: 'CIF - Cost, Insurance and Freight',
};

// Any-mode incoterms (ICC Incoterms 2020)
const ANY_MODE_INCOTERMS: Incoterm[] = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];
// Maritime-only incoterms
const MARITIME_INCOTERMS: Incoterm[] = ['FAS', 'FOB', 'CFR', 'CIF'];

// Incoterms available per shipment type
export const INCOTERMS_BY_SHIPMENT_TYPE: Record<string, Incoterm[]> = {
  fcl: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
  lcl: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
  air: ANY_MODE_INCOTERMS,
  road: ANY_MODE_INCOTERMS,
  roro: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
  break_bulk: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
};

// ============================================================================
// Milestone Types
// ============================================================================

export type MilestoneType = 'documentation' | 'physical' | 'financial' | 'regulatory';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

export interface MilestoneDefinition {
  id: string;
  name: string;
  description?: string;
  owner?: string;
  updater?: string;
  type: MilestoneType;
  order: number;
}

export interface MilestonePhase {
  id: string;
  name: string;
  order: number;
  milestones: MilestoneDefinition[];
}

export interface MilestoneConfig {
  id: string;
  companyId: string;
  phases: MilestonePhase[];
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentMilestone {
  id: string;
  companyId: string;
  shipmentId: string;
  phaseId: string;
  phaseName: string;
  milestoneDefinitionId: string;
  milestoneName: string;
  milestoneType: MilestoneType;
  owner?: string;
  updater?: string;
  status: MilestoneStatus;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
  order: number;
  phaseOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveMilestoneConfigDto {
  phases: {
    id?: string;
    name: string;
    order: number;
    milestones: {
      id?: string;
      name: string;
      description?: string;
      owner?: string;
      updater?: string;
      type: MilestoneType;
      order: number;
    }[];
  }[];
}

export interface UpdateShipmentMilestoneDto {
  status?: MilestoneStatus;
  notes?: string;
}

export const MILESTONE_TYPE_LABELS: Record<MilestoneType, string> = {
  documentation: 'Documentation',
  physical: 'Physical',
  financial: 'Financial',
  regulatory: 'Regulatory',
};

export const MILESTONE_STATUS_LABELS: Record<MilestoneStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  skipped: 'Skipped',
};

export const DEFAULT_MILESTONE_PHASES: { name: string; order: number }[] = [
  { name: 'Pre-Shipment & Booking', order: 0 },
  { name: 'Export Origin Handling', order: 1 },
  { name: 'Main Transit', order: 2 },
  { name: 'Import Destination Operations', order: 3 },
  { name: 'Post-Delivery & Financials', order: 4 },
];
